import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { renderableType } from '../proptypes/prop-types';
import './textField.scss';
import { buildClassNames, buildClassNameModifiers } from '../util/utils';

/** Block */
const blockBaseClassName = 'textField';

/** Elements */
const containerClassName = 'textField__container';
const inputClassName = 'textField__input';
const messageClassName = 'textField__message';
const labelClassName = 'textField__label';

/** Modifier */
const errorModifier = 'error';
const multiModifier = 'multi';
const focusedModifier = 'focused';
const noLabelModifier = 'noLabel';
const hasValueModifier = 'hasValue';

/** A component for handling text input */
export class TextField extends Component {
  static propTypes = {
    /** @ignore */
    className: PropTypes.string,
    /** Auto-complete capabilities to assign to the text input */
    autoComplete: PropTypes.string,
    /** Determines whether the component will be rendered in a compact form */
    compact: PropTypes.bool,
    /** Indicates whether the component is disabled */
    disabled: PropTypes.bool,
    /** Indicates whether the component is in an error state */
    error: PropTypes.bool,
    /** Message displayed when in an error state */
    errorMessage: renderableType,
    /** The id assigned to the text input */
    id: PropTypes.string,
    /** The label applied to the text input */
    label: PropTypes.string,
    /** Maximum characters that can be entered */
    maxLength: PropTypes.number,
    /** Indicates max value when `type="number"` */
    max: PropTypes.number,
    /** Indicates min value when `type="number"` */
    min: PropTypes.number,
    /** Default message displayed when not in an error state */
    message: renderableType,
    /** Indicates whether the input supports multiple lines of text */
    multi: PropTypes.bool,
    /** Text identifier applied to the text input */
    name: PropTypes.string.isRequired,
    /** Function executed when text is entered */
    onChange: PropTypes.func,
    /** Placeholder text for the input */
    placeholder: PropTypes.string,
    /** Indicates that the input is read-only */
    readOnly: PropTypes.bool,
    /** Indicates whether form submission will fail if a value is not present */
    required: PropTypes.bool,
    /** Number of rows to display on multi-line inputs (ignored if `multi` is false) */
    rows: PropTypes.number,
    /** Indicates the increment size when `typer="number"` */
    step: PropTypes.number,
    /** Text displayed on hover */
    title: PropTypes.string,
    /** The type of text being entered */
    type: PropTypes.oneOf(['text', 'password', 'number']),
    /** The text that has been entered */
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ])),
    ]),
  };

  static defaultProps = {
    compact: false,
    value: '',
    type: 'text',
    error: false,
    errorMessage: 'Invalid input',
    multi: false,
    readOnly: false,
    required: false,
    rows: 1,
    onChange: undefined,
  };

  constructor(props) {
    super(props);
    this.state = { focused: false };
  }

  handleChange = (event) => {
    this.props.onChange(event);
  };

  handleFocus = () => {
    this.setState({
      ...this.state,
      focused: true,
    });
  };

  handleBlur = () => {
    this.setState({
      ...this.state,
      focused: false,
    });
  };

  render() {
    const {
      className,
      autoComplete,
      compact,
      disabled,
      error,
      errorMessage,
      id,
      label,
      maxLength,
      max,
      min,
      message,
      multi,
      name,
      onChange,
      placeholder,
      readOnly,
      required,
      rows,
      step,
      title,
      type,
      value,
      ...other
    } = this.props;

    const { focused } = this.state;

    const blockClassNames = buildClassNames(
      className,
      buildClassNameModifiers(
        blockBaseClassName,
        { disabled },
        { compact },
        error && errorModifier,
        focused && focusedModifier,
      ),
      blockBaseClassName,
    );
    const containerClassNames = buildClassNames(
      buildClassNameModifiers(
        containerClassName,
        { disabled },
        { compact },
        { readOnly },
        error && errorModifier,
        focused && focusedModifier,
      ),
      containerClassName,
    );
    const labelClassNames = buildClassNames(
      buildClassNameModifiers(
        labelClassName,
        { disabled },
        { compact },
        value && hasValueModifier,
        error && errorModifier,
        focused && focusedModifier,
      ),
      labelClassName,
    );
    const inputClassNames = buildClassNames(
      buildClassNameModifiers(
        inputClassName,
        { disabled },
        { compact },
        type,
        multi && multiModifier,
        !label && noLabelModifier,
      ),
      inputClassName,
    );
    const messageClassNames = buildClassNames(
      buildClassNameModifiers(
        messageClassName,
        { disabled },
        { compact },
        error && errorModifier,
      ),
      messageClassName,
    );
    const FieldType = multi ? 'textarea' : 'input';
    const autoCompleteType = autoComplete || (type === 'password' && 'password') || 'on';

    return (
      <div className={blockClassNames}>
        {!(compact && (value || focused)) &&
          label && (
            <label className={labelClassNames} htmlFor={id} title={label}>{label}</label>
          )}
        <div className={containerClassNames}>
          <FieldType
            autoComplete={autoCompleteType}
            className={inputClassNames}
            disabled={disabled}
            id={id}
            maxLength={maxLength}
            max={max}
            min={min}
            name={name}
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            placeholder={placeholder}
            readOnly={readOnly}
            required={required}
            rows={rows}
            step={step}
            title={title}
            type={type}
            value={value}
            {...other}
          />
        </div>
        {(!compact || (error && errorMessage) || message) &&
          <div className={messageClassNames}>
            {error ? errorMessage : message}
          </div>
        }
      </div>
    );
  }
}
